import React from 'react';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';
import { Form, Input } from 'semantic-ui-react';
import isNumeric from 'validator/es/lib/isNumeric';
import {
  clientError, firstError, firstResponseError, pruneServerErrors,
  invalidUrlCharacters, validationClasses,
} from '@/utils/errors';
import { states } from '@/constants/lifecycle';
import fields, { globalFieldLengths } from '@/constants/fields';
import urlDenyCharacters from '@/constants/validation';
import FormValidationError from '@/components/errors/FormValidationError';
import Dialog from '@/components/dialogs/Dialog';
import VisibilityDropdown from '@/components/VisibilityDropdown';
import MarkdownEditor from '@/components/text-editors/MarkdownEditor';
import {
  hideSaveSearch, createSearch, updateSearch, updateActiveSearchProperties,
} from '../actions';
import { searchPermissions } from '../constants';

const SaveSearchDialog = ({
  canChangeVisibility,
  code,
  createErrors,
  createStatus,
  description,
  hide,
  isNew,
  name,
  postData,
  putData,
  theme,
  updateErrors,
  updateProperties,
  updateStatus,
  visibility,
}) => {
  const intl = useIntl();
  const isSaving = () => (isNew ? createStatus === states.PENDING : updateStatus === states.PENDING);

  const errors = () => (isNew ? createErrors : updateErrors);

  const setErrors = (newErrors) => (isNew
    ? updateProperties({ createErrors: newErrors })
    : updateProperties({ updateErrors: newErrors }));

  const setStatus = (status) => (isNew
    ? updateProperties({ createStatus: status })
    : updateProperties({ updateStatus: status }));

  const handleClose = () => {
    hide();
  };

  const nameChange = (e) => {
    updateProperties({ name: e.target.value });
  };

  const descriptionChange = (value) => {
    updateProperties({ description: value });
  };

  const codeChange = (e) => {
    updateProperties({ code: e.target.value });
  };

  const handleDismiss = () => {
    setErrors(pruneServerErrors(errors()));
  };

  const validateForm = () => {
    const validationErrors = [];

    if (code && (isNumeric(code) || invalidUrlCharacters(code))) {
      validationErrors.push(
        clientError(
          { entity: 'search' },
          intl.formatMessage({ id: 'save.search.validation.code.title' }),
          intl.formatMessage({ id: 'validation.invalid.code' }, { characters: urlDenyCharacters }),
          [fields.CODE],
        ),
      );
    }
    if (description && description.length > globalFieldLengths.description) {
      validationErrors.push(
        clientError(
          { entity: 'search' },
          intl.formatMessage({ id: 'save.search.validation.description.title' }),
          intl.formatMessage({ id: 'validation.max.length' }, { maximum: globalFieldLengths.description }),
          [fields.DESCRIPTION],
        ),
      );
    }

    return validationErrors;
  };

  const checkForErrors = () => {
    const validationErrors = validateForm();
    setErrors(validationErrors);

    return validationErrors;
  };

  const submitForm = () => {
    setStatus(states.PENDING);
    const validationErrors = checkForErrors();

    if (validationErrors.length > 0) {
      setStatus(states.NONE);
      return;
    }

    if (isNew) {
      postData();
    } else {
      putData();
    }
  };

  const codeError = updateErrors ? errors().find((e) => (e.fields.includes(fields.CODE))) : null;
  const nameError = updateErrors ? errors().find((e) => (e.fields.includes(fields.NAME))) : null;
  const descriptionError = updateErrors ? errors().find((e) => (e.fields.includes(fields.DESCRIPTION))) : null;

  return (
    <Dialog
      className="aridhia-save-search-dialog"
      error={firstResponseError(errors())}
      icon="search"
      inProgress={isSaving()}
      minimumDataEntryMet={name}
      onClose={handleClose}
      onDismiss={handleDismiss}
      onSave={submitForm}
      saveMessageId={isNew ? 'save.search.create.button' : 'actions.modify'}
      theme={theme}
      title={intl.formatMessage({ id: 'save.search.create.title' })}
    >
      <Form autoComplete="off" onSubmit={submitForm} error={firstError(errors()) !== null}>
        <FormValidationError error={codeError} htmlFor="search_code" labelId="save.search.code">
          <Input
            id="search_code"
            size="large"
            disabled={isSaving() || !isNew}
            type="custom"
            placeholder={intl.formatMessage({ id: 'save.search.code.placeholder' })}
            value={code || ''}
            maxLength={globalFieldLengths.code}
            onChange={codeChange}
            className={validationClasses(codeError)}
          />
        </FormValidationError>
        <FormValidationError error={nameError} htmlFor="search_name" labelId="save.search.name">
          <Input
            id="search_name"
            autoFocus
            disabled={isSaving()}
            type="custom"
            size="large"
            placeholder={intl.formatMessage({ id: 'save.search.name.placeholder' })}
            value={name}
            maxLength={globalFieldLengths.name}
            onChange={nameChange}
            className={validationClasses(nameError)}
          />
        </FormValidationError>
        <FormValidationError htmlFor="search_visibility" labelId="save.search.visibility">
          {canChangeVisibility && (
          <VisibilityDropdown
            id="search_visibility"
            disabled={isSaving()}
            onChange={updateProperties}
            visibility={visibility}
          />
          )}
          {!canChangeVisibility && (
          <Input
            id="search_visibility"
            disabled
            type="custom"
            size="large"
            value={intl.formatMessage({ id: `visibility.${visibility}` })}
          />
          )}
        </FormValidationError>
        <FormValidationError error={descriptionError} htmlFor="search_description" labelId="save.search.description">
          <MarkdownEditor
            id="search_description"
            disabled={isSaving()}
            value={description}
            onChange={descriptionChange}
            maxEditorHeight={80}
            minPreviewHeight={94}
            className={validationClasses(descriptionError)}
          />
        </FormValidationError>
      </Form>
    </Dialog>
  );
};

const mapStateToProps = ({
  search: {
    activeSearch: {
      isNew, code, name, visibility, createErrors, createStatus, updateErrors, updateStatus, description, permissions,
    },
  },
}) => ({
  canChangeVisibility: permissions[searchPermissions.VISIBILITY],
  code,
  createErrors,
  createStatus,
  description,
  isNew,
  name,
  visibility,
  updateErrors,
  updateStatus,
});

const mapDispatchToProps = {
  hide: hideSaveSearch,
  updateProperties: updateActiveSearchProperties,
  postData: createSearch,
  putData: updateSearch,
};

export default connect(mapStateToProps, mapDispatchToProps)(SaveSearchDialog);
